/**
 * Overview tab component — executive-ready intelligence view
 *
 * Layout (top to bottom):
 * 1. Executive Summary Card (above fold)
 * 2. Risk Scorecard + What Changed (side by side on desktop)
 * 3. Stock/Revenue charts (public companies)
 * 4. Key Metrics grid
 * 5. Strategic Overview + Synthesis
 * 6. Entity Hierarchy + KB Dashboard
 * 7. Reports
 */

import { useState } from 'react';
import type {
  CompanyOverview,
  CompanySnapshot,
  MetricSnapshot,
  RecentInitiative,
  StrategicOverview,
  Synthesis,
} from '../../../types';
import { useStockData, useQuarterlyRevenue } from '../../../hooks/useCompanies';
import { StockChart } from '../StockChart';
import { RevenueChart } from '../RevenueChart';
import { ReportsPanel } from '../ReportsPanel';
import { KBDashboard } from '../KBDashboard';
import { ExecutiveSummaryCard } from '../cards/ExecutiveSummaryCard';
import { RiskScorecardCard } from '../cards/RiskScorecardCard';
import { WhatChangedCard } from '../cards/WhatChangedCard';

interface OverviewTabProps {
  companyId: string;
  overview: CompanyOverview;
}

function formatValue(value: number | string, unit?: string): string {
  if (typeof value === 'string') return value;

  // Format large numbers
  if (unit === 'USD') {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    return `$${value.toLocaleString()}`;
  }

  if (unit === 'employees') {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString();
  }

  return value.toLocaleString();
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const colors = {
    high: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-red-100 text-red-800',
  };

  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
        colors[confidence as keyof typeof colors] || colors.low
      }`}
    >
      {confidence}
    </span>
  );
}

function MetricCard({ name, metric }: { name: string; metric: MetricSnapshot }) {
  const labels: Record<string, string> = {
    revenue: 'Revenue',
    market_cap: 'Market Cap',
    employee_count: 'Employees',
    net_income: 'Net Income',
    ebitda: 'EBITDA',
    ebitda_margin: 'EBITDA Margin',
    profit_margin: 'Profit Margin',
    revenue_growth: 'Revenue Growth',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-500">{labels[name] || name}</span>
        <ConfidenceBadge confidence={metric.confidence} />
      </div>
      <div className="mt-2">
        <span className="text-2xl font-semibold text-gray-900">
          {formatValue(metric.value, metric.unit)}
        </span>
      </div>
      <div className="mt-1 flex items-center text-xs text-gray-500">
        <span>{metric.period}</span>
        {metric.is_estimated && <span className="ml-2 text-yellow-600">(Estimated)</span>}
      </div>
    </div>
  );
}

function StrategicOverviewSection({ overview }: { overview: StrategicOverview }) {
  return (
    <section className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Strategic Overview</h2>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Business Description */}
        {overview.business_description && (
          <div className="rounded-lg border border-gray-200 bg-white p-5 lg:col-span-2">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Business Description</h3>
            <p className="text-gray-900">{overview.business_description}</p>
          </div>
        )}

        {/* Value Proposition */}
        {overview.value_proposition && (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Value Proposition</h3>
            <p className="text-gray-900">{overview.value_proposition}</p>
          </div>
        )}

        {/* Strategic Direction */}
        {overview.strategic_direction && (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Strategic Direction</h3>
            <p className="text-gray-900">{overview.strategic_direction}</p>
          </div>
        )}

        {/* Target Markets & Segments */}
        {(overview.target_markets?.length || overview.market_segments?.length) && (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Market Focus</h3>
            {overview.target_markets && overview.target_markets.length > 0 && (
              <div className="mb-3">
                <span className="text-xs text-gray-400 uppercase tracking-wide">
                  Target Markets
                </span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {overview.target_markets.map((market) => (
                    <span
                      key={market}
                      className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700"
                    >
                      {market}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {overview.market_segments && overview.market_segments.length > 0 && (
              <div>
                <span className="text-xs text-gray-400 uppercase tracking-wide">Segments</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {overview.market_segments.map((segment) => (
                    <span
                      key={segment}
                      className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-purple-700"
                    >
                      {segment}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Key Differentiators */}
        {overview.key_differentiators && overview.key_differentiators.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Key Differentiators</h3>
            <ul className="space-y-2">
              {overview.key_differentiators.map((diff) => (
                <li key={diff} className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-gray-900 text-sm">{diff}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Competitive Landscape */}
        {overview.primary_competitors && overview.primary_competitors.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Primary Competitors</h3>
            <div className="space-y-3">
              {overview.primary_competitors.map((comp) => (
                <div
                  key={comp.name}
                  className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0"
                >
                  <div>
                    <span className="font-medium text-gray-900">{comp.name}</span>
                    {comp.key_differentiator && (
                      <p className="text-xs text-gray-500 mt-0.5">{comp.key_differentiator}</p>
                    )}
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      comp.relationship === 'direct'
                        ? 'bg-red-50 text-red-700'
                        : comp.relationship === 'adjacent'
                          ? 'bg-yellow-50 text-yellow-700'
                          : 'bg-gray-50 text-gray-700'
                    }`}
                  >
                    {comp.relationship}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Win Patterns */}
        {overview.win_reasons && overview.win_reasons.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Why They Win</h3>
            <ul className="space-y-2">
              {overview.win_reasons.map((reason) => (
                <li key={reason} className="flex items-start">
                  <span className="text-green-500 mr-2">▲</span>
                  <span className="text-gray-900 text-sm">{reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Loss Patterns */}
        {overview.loss_reasons && overview.loss_reasons.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Why They Lose</h3>
            <ul className="space-y-2">
              {overview.loss_reasons.map((reason) => (
                <li key={reason} className="flex items-start">
                  <span className="text-red-500 mr-2">▼</span>
                  <span className="text-gray-900 text-sm">{reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Customer Profile */}
        {(overview.customer_profile ||
          (overview.notable_customers && overview.notable_customers.length > 0)) && (
          <div className="rounded-lg border border-gray-200 bg-white p-5 lg:col-span-2">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Customer Profile</h3>
            {overview.customer_profile && (
              <p className="text-gray-900 mb-3">{overview.customer_profile}</p>
            )}
            {overview.notable_customers && overview.notable_customers.length > 0 && (
              <div>
                <span className="text-xs text-gray-400 uppercase tracking-wide">
                  Notable Customers
                </span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {overview.notable_customers.map((customer) => (
                    <span
                      key={customer}
                      className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700"
                    >
                      {customer}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confidence indicator */}
      {overview.confidence !== undefined && (
        <div className="text-xs text-gray-400 text-right">
          Data confidence: {Math.round(overview.confidence * 100)}%
          {overview.last_updated &&
            ` • Last updated: ${new Date(overview.last_updated).toLocaleDateString()}`}
        </div>
      )}
    </section>
  );
}

function RevenueTrendIndicator({ trend, growthRate }: { trend?: string; growthRate?: string }) {
  if (!trend || trend === 'unknown') return null;

  const config = {
    growing: { arrow: '\u2191', color: 'text-green-600', bg: 'bg-green-50' },
    declining: { arrow: '\u2193', color: 'text-red-600', bg: 'bg-red-50' },
    flat: { arrow: '\u2192', color: 'text-gray-500', bg: 'bg-gray-50' },
  };

  const c = config[trend as keyof typeof config];
  if (!c) return null;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${c.color} ${c.bg}`}
    >
      <span>{c.arrow}</span>
      {growthRate && <span>{growthRate}</span>}
    </span>
  );
}

function CompanySnapshotCard({ snapshot }: { snapshot: CompanySnapshot }) {
  const entries: { label: string; value: string }[] = [];
  if (snapshot.market_position)
    entries.push({ label: 'Market Position', value: snapshot.market_position });
  if (snapshot.estimated_revenue)
    entries.push({ label: 'Revenue', value: snapshot.estimated_revenue });
  if (snapshot.estimated_valuation)
    entries.push({ label: 'Valuation', value: snapshot.estimated_valuation });
  if (snapshot.employee_count) entries.push({ label: 'Employees', value: snapshot.employee_count });
  if (snapshot.customer_count) entries.push({ label: 'Customers', value: snapshot.customer_count });
  if (snapshot.install_count)
    entries.push({ label: 'Install Base', value: snapshot.install_count });
  if (snapshot.years_in_operation)
    entries.push({ label: 'Years Operating', value: String(snapshot.years_in_operation) });

  const hasInvestors = snapshot.primary_investors && snapshot.primary_investors.length > 0;
  const hasPartners = snapshot.primary_partners && snapshot.primary_partners.length > 0;

  if (entries.length === 0 && !hasInvestors && !hasPartners) return null;

  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50/30 p-5">
      <h3 className="text-sm font-medium text-gray-500 mb-3">Company Snapshot</h3>
      {entries.length > 0 && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 mb-3">
          {entries.map((e) => (
            <div key={e.label}>
              <dt className="text-xs text-gray-400">{e.label}</dt>
              <dd className="text-sm font-medium text-gray-900">{e.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {hasInvestors && (
        <div className="mb-2">
          <span className="text-xs text-gray-400">Investors</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {snapshot.primary_investors!.map((inv) => (
              <span
                key={inv}
                className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
              >
                {inv}
              </span>
            ))}
          </div>
        </div>
      )}
      {hasPartners && (
        <div>
          <span className="text-xs text-gray-400">Partners</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {snapshot.primary_partners!.map((p) => (
              <span
                key={p}
                className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function getInitiativeText(initiative: string | RecentInitiative): {
  description: string;
  analysis?: string;
  date?: string;
} {
  if (typeof initiative === 'string') return { description: initiative };
  return initiative;
}

function SynthesisSection({ synthesis }: { synthesis: Synthesis }) {
  const { profile, competitive } = synthesis;

  const hasProfile =
    profile &&
    (profile.business_model || profile.revenue_description || profile.recent_initiatives?.length);
  const hasCompetitive =
    competitive &&
    (competitive.market_dynamics ||
      competitive.competitive_position ||
      competitive.company_snapshot);

  if (!hasProfile && !hasCompetitive) return null;

  const snapshot = competitive?.company_snapshot;

  return (
    <section className="space-y-6">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-gray-900">AI Research Analysis</h2>
        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
          Synthesized
        </span>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Row 1: Business Model | Revenue */}
        {profile?.business_model && (
          <div className="rounded-lg border border-purple-200 bg-purple-50/30 p-5">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Business Model</h3>
            <p className="text-gray-900 text-sm">{profile.business_model}</p>
          </div>
        )}

        {profile?.revenue_description && (
          <div className="rounded-lg border border-purple-200 bg-purple-50/30 p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">Revenue</h3>
              <RevenueTrendIndicator
                trend={profile.revenue_trend}
                growthRate={profile.revenue_growth_rate}
              />
            </div>
            <p className="text-gray-900 text-sm">{profile.revenue_description}</p>
          </div>
        )}

        {/* Row 2: Company Snapshot | Market Dynamics */}
        {snapshot && <CompanySnapshotCard snapshot={snapshot} />}

        {!snapshot && competitive?.competitive_position && (
          <div className="rounded-lg border border-purple-200 bg-purple-50/30 p-5">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Competitive Position</h3>
            <p className="text-gray-900 text-sm">{competitive.competitive_position}</p>
          </div>
        )}

        {competitive?.market_dynamics && (
          <div className="rounded-lg border border-purple-200 bg-purple-50/30 p-5">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Market Dynamics</h3>
            <p className="text-gray-900 text-sm">{competitive.market_dynamics}</p>
          </div>
        )}

        {/* Row 3: Recent Initiatives (full width) */}
        {profile?.recent_initiatives && profile.recent_initiatives.length > 0 && (
          <div className="rounded-lg border border-purple-200 bg-purple-50/30 p-5 lg:col-span-2">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Recent Initiatives</h3>
            <ul className="space-y-3">
              {profile.recent_initiatives.map((initiative, i) => {
                const { description, analysis, date } = getInitiativeText(initiative);
                return (
                  <li key={i} className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-0.5">&#8226;</span>
                    <div>
                      <span className="text-gray-900 text-sm">
                        {date && <span className="text-gray-400 mr-1.5">{date}</span>}
                        {description}
                      </span>
                      {analysis && (
                        <p className="text-gray-500 text-sm italic mt-0.5">{analysis}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Confidence indicator */}
      <div className="text-xs text-gray-400 text-right">
        Synthesis confidence:{' '}
        {Math.round((profile?.confidence ?? competitive?.confidence ?? 0) * 100)}%
      </div>
    </section>
  );
}

export function OverviewTab({ companyId, overview }: OverviewTabProps) {
  const { company, key_metrics, entity_hierarchy, kb_status, strategic_overview } = overview;
  const synthesis = overview.synthesis;
  const executiveSummary = overview.executive_summary;
  const riskScorecard = overview.risk_scorecard;
  const recentSignals = overview.recent_signals;
  const isPublic = company.company_type === 'public' && !!company.ticker;

  // Stock chart period state - default to 6 months for broader context
  const [stockPeriod, setStockPeriod] = useState('6mo');

  // Fetch stock data for public companies only
  const { data: stockData, isLoading: stockLoading } = useStockData(
    companyId,
    stockPeriod,
    isPublic
  );

  // Fetch quarterly revenue for public companies
  const { data: revenueData, isLoading: revenueLoading } = useQuarterlyRevenue(companyId, isPublic);

  return (
    <div className="space-y-8">
      {/* TOP: Executive Summary (above fold) */}
      {executiveSummary && <ExecutiveSummaryCard summary={executiveSummary} />}

      {/* MIDDLE: Risk Scorecard + What Changed (side by side on desktop) */}
      {(riskScorecard?.dimensions?.length || recentSignals?.length !== undefined) && (
        <div className="grid gap-6 lg:grid-cols-2">
          {riskScorecard && riskScorecard.dimensions.length > 0 && (
            <RiskScorecardCard scorecard={riskScorecard} />
          )}
          <WhatChangedCard signals={recentSignals || []} />
        </div>
      )}

      {/* Stock Chart for public companies */}
      {isPublic && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Stock Performance</h2>
          {stockData ? (
            <StockChart
              data={stockData}
              isLoading={stockLoading}
              selectedPeriod={stockPeriod}
              onPeriodChange={setStockPeriod}
              marketCap={key_metrics.market_cap?.value as number | undefined}
              revenue={key_metrics.revenue?.value as number | undefined}
            />
          ) : stockLoading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="animate-pulse">
                <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
                <div className="h-48 bg-gray-100 rounded" />
              </div>
            </div>
          ) : null}
        </section>
      )}

      {/* Revenue Chart for public companies */}
      {isPublic && (
        <section>
          {revenueData ? (
            <RevenueChart data={revenueData} isLoading={revenueLoading} />
          ) : revenueLoading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="animate-pulse">
                <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
                <div className="h-64 bg-gray-100 rounded" />
              </div>
            </div>
          ) : null}
        </section>
      )}

      {/* Key Metrics */}
      {Object.keys(key_metrics).length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(key_metrics).map(([name, metric]) => (
              <MetricCard key={name} name={name} metric={metric} />
            ))}
          </div>
        </section>
      )}

      {/* Strategic Overview */}
      {strategic_overview && <StrategicOverviewSection overview={strategic_overview} />}

      {/* AI Synthesis Section */}
      {synthesis && <SynthesisSection synthesis={synthesis} />}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Entity Hierarchy */}
        {entity_hierarchy.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Related Entities</h2>
            <div className="rounded-lg border border-gray-200 bg-white">
              <ul className="divide-y divide-gray-100">
                {entity_hierarchy.map((entity) => (
                  <li key={entity.name} className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-900">{entity.name}</span>
                        {entity.domain && (
                          <span className="ml-2 text-sm text-gray-500">{entity.domain}</span>
                        )}
                      </div>
                      {entity.relevance && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                          {entity.relevance}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* KB Status */}
        {kb_status && (
          <section className="lg:col-span-2">
            <KBDashboard kbStatus={kb_status} companyName={company.name} />
          </section>
        )}
      </div>

      {/* Reports Section */}
      <section className="mt-8">
        <ReportsPanel companyId={companyId} companyName={company.name} isPublic={isPublic} />
      </section>

      {/* No data state */}
      {Object.keys(key_metrics).length === 0 &&
        entity_hierarchy.length === 0 &&
        !executiveSummary && (
          <div className="text-center py-12">
            <p className="text-gray-500">No overview data available yet.</p>
            <p className="text-sm text-gray-400 mt-2">The knowledge base is being populated.</p>
          </div>
        )}
    </div>
  );
}

export default OverviewTab;
